/**
 * Sessions list loader: the single place that reads `GET /sessions/mine`,
 * persists the result to the per-user local cache, and lets the app PREFETCH
 * the list right after sign-in / on app resume so the Sessions screen usually
 * opens onto data that is already in flight (or already landed).
 *
 * The cached snapshot is exactly what the Sessions screen renders (the
 * server-lensed list + the audience label) — see `services/local-cache.ts`
 * for the per-user isolation and sign-out wipe guarantees.
 */

import type {
  CoreSessionStatus,
  JourneyStatus,
  Session,
  SessionLifecycleState,
} from '@shared/sessions/types'
import { getLocalCacheEpoch, readLocalCache, writeLocalCache } from '../../../services/local-cache'
import { getSessionsRepository, type ApiSessionsRepository } from './sessions-repository-provider'
import type { SessionsAudience } from './sessions-api-client'

export const SESSIONS_CACHE_NAME = 'sessions-list'

export interface SessionsListSnapshot {
  sessions: Session[]
  audience: SessionsAudience
}

/**
 * The ONLY session fields that ever reach disk — exactly what a list row
 * renders (names as displayed, times, service/telehealth kind, status badge
 * inputs, tracked km). Deliberately absent: notes, checklists / progress-note
 * drafts, emails, address / `locationText`, geocode, check-in/out coordinates,
 * drive segments, journey pings, travel, cancellation reason/notes. The detail
 * view always fetches live for those.
 */
export interface CachedSessionRow {
  uuid: string
  providerName: string
  memberName: string
  serviceType: string
  faceToFace: boolean
  nonFaceToFaceSessionType: string | null
  start: number
  end: number
  timezone: string
  coreStatus: CoreSessionStatus
  lifecycleState: SessionLifecycleState
  source: 'ahp' | 'callback'
  kmTracked: number | null
  /** A member's cancellation request is awaiting the provider (boolean only — no reason/note/email). */
  cancellationPending: boolean
  /** Journey status only (drives "On the way" / "Arrived") — never pings or coordinates. */
  journeyStatus: JourneyStatus | null
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '')
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)

/** Whitelist projection: copies ONLY the fields above, whatever the input carries. */
export function toCachedSessionRow(s: Session): CachedSessionRow {
  return {
    uuid: s.uuid,
    providerName: str(s.providerName),
    memberName: str(s.memberName),
    serviceType: str(s.serviceType),
    faceToFace: s.faceToFace !== false,
    nonFaceToFaceSessionType: typeof s.nonFaceToFaceSessionType === 'string' ? s.nonFaceToFaceSessionType : null,
    start: s.start,
    end: s.end,
    timezone: str(s.timezone),
    coreStatus: s.coreStatus,
    lifecycleState: s.lifecycleState,
    source: s.source === 'callback' ? 'callback' : 'ahp',
    kmTracked: num(s.kmTracked),
    cancellationPending: s.cancellationRequested != null,
    journeyStatus: s.journey?.status ?? null,
  }
}

/** Validate an untrusted stored row and rebuild a list-only `Session` (everything else empty). */
function fromCachedSessionRow(raw: unknown): Session | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Partial<CachedSessionRow>
  const start = num(r.start)
  const end = num(r.end)
  if (typeof r.uuid !== 'string' || !r.uuid || start == null || end == null) return null
  const journeyStatus = typeof r.journeyStatus === 'string' ? (r.journeyStatus as JourneyStatus) : null
  return {
    uuid: r.uuid,
    seriesUuid: null,
    providerEmail: '',
    providerName: str(r.providerName),
    memberEmail: '',
    memberName: str(r.memberName),
    locationText: '',
    geo: null,
    serviceType: str(r.serviceType),
    faceToFace: r.faceToFace !== false,
    nonFaceToFaceSessionType:
      typeof r.nonFaceToFaceSessionType === 'string' ? r.nonFaceToFaceSessionType : null,
    start,
    end,
    timezone: str(r.timezone),
    coreStatus: (r.coreStatus ?? 'unknown') as CoreSessionStatus,
    notes: '',
    checkins: [],
    lifecycleState: (r.lifecycleState ?? 'scheduled') as SessionLifecycleState,
    actualStart: null,
    actualEnd: null,
    checkIn: null,
    checkOut: null,
    kmTracked: num(r.kmTracked),
    driveSegments: [],
    noteStatus: 'none',
    ...(r.source === 'callback' ? { source: 'callback' as const } : {}),
    ...(r.cancellationPending === true
      ? {
          cancellationRequested: {
            requestedAt: 0,
            requestedByEmail: null,
            reason: 'other' as const,
          },
        }
      : {}),
    ...(journeyStatus
      ? { journey: { status: journeyStatus, pings: [], startedAt: null } }
      : {}),
  } as Session
}

/** A prefetch younger than this is reused by the screen instead of a second request. */
export const SESSIONS_PREFETCH_MAX_AGE_MS = 15_000

const AUDIENCES: readonly SessionsAudience[] = [
  'provider',
  'member',
  'linked_member_user',
  'linked_independent',
]

export function resolveRepositoryAudience(): SessionsAudience {
  const repo = getSessionsRepository()
  if ('getAudience' in repo && typeof (repo as ApiSessionsRepository).getAudience === 'function') {
    return (repo as ApiSessionsRepository).getAudience()
  }
  return 'provider'
}

let prefetched: { userId: string; at: number; promise: Promise<SessionsListSnapshot> } | null = null

async function fetchAndPersist(userId: string | null): Promise<SessionsListSnapshot> {
  // Captured BEFORE the request: a sign-out / account switch while it is in
  // flight makes the late response a no-op for the cache.
  const epoch = getLocalCacheEpoch()
  const sessions = await getSessionsRepository().listMySessions()
  const snapshot: SessionsListSnapshot = { sessions, audience: resolveRepositoryAudience() }
  if (userId) void writeLocalCache(userId, SESSIONS_CACHE_NAME, toStoredSnapshot(snapshot), { epoch })
  return snapshot
}

/**
 * Start loading the list in the background (fire-and-forget). Safe to call on
 * every sign-in / resume: a fresh prefetch for the same user is not repeated.
 */
export function prefetchSessionsList(userId: string): void {
  const now = Date.now()
  if (prefetched && prefetched.userId === userId && now - prefetched.at < SESSIONS_PREFETCH_MAX_AGE_MS) {
    return
  }
  const entry = { userId, at: now, promise: fetchAndPersist(userId) }
  prefetched = entry
  entry.promise.catch(() => {
    // A failed prefetch is not reused — the screen does its own load and
    // surfaces the error + retry.
    if (prefetched === entry) prefetched = null
  })
}

/**
 * Load the list, consuming a fresh prefetch for this user when one exists
 * (single-use: the next call hits the network again).
 */
export async function loadSessionsList(userId: string | null): Promise<SessionsListSnapshot> {
  const pre = prefetched
  if (pre && userId && pre.userId === userId && Date.now() - pre.at < SESSIONS_PREFETCH_MAX_AGE_MS) {
    prefetched = null
    try {
      return await pre.promise
    } catch {
      /* fall through to a normal load */
    }
  }
  return fetchAndPersist(userId)
}

/** Drop any in-flight / landed prefetch (sign-out, account switch). */
export function resetSessionsPrefetch(): void {
  prefetched = null
}

/** The only shape ever written: whitelisted rows + audience. */
function toStoredSnapshot(snapshot: SessionsListSnapshot): {
  sessions: CachedSessionRow[]
  audience: SessionsAudience
} {
  return { sessions: snapshot.sessions.map(toCachedSessionRow), audience: snapshot.audience }
}

/** Last snapshot shown to `userId` (null on miss / expiry / wrong user / bad shape). */
export async function readCachedSessionsList(
  userId: string | null | undefined,
): Promise<{ snapshot: SessionsListSnapshot; at: number } | null> {
  const hit = await readLocalCache<unknown>(userId, SESSIONS_CACHE_NAME)
  if (!hit || !hit.data || typeof hit.data !== 'object') return null
  const v = hit.data as { sessions?: unknown; audience?: unknown }
  if (!Array.isArray(v.sessions) || typeof v.audience !== 'string' || !AUDIENCES.includes(v.audience as SessionsAudience)) {
    return null
  }
  const sessions = v.sessions.map(fromCachedSessionRow).filter((x): x is Session => x !== null)
  return { snapshot: { sessions, audience: v.audience as SessionsAudience }, at: hit.at }
}

/** Re-persist after an in-place patch (e.g. check-in) so the next open isn't stale. */
export function persistSessionsList(userId: string | null | undefined, snapshot: SessionsListSnapshot): void {
  void writeLocalCache(userId, SESSIONS_CACHE_NAME, toStoredSnapshot(snapshot))
}
